import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import {
  transformMimeType,
  validateFileHeader,
  imageAllowedExtensions,
} from '../../../../helpers/fileValidation'
import Icon from '../../../Icons'

const FILE_MAX_SIZE = 3000000 // 3MB

const MediaUploadInput = ({ handleFieldChange, formState }) => {
  const { t } = useTranslation(['create-event'])
  const [error, setError] = useState(false)

  const validateFile = (file) => {
    handleFieldChange({ target: { name: 'photo', value: undefined } })
    if (file.size > FILE_MAX_SIZE) {
      return setError(true)
    }
    const fileType = transformMimeType(file.type)
    if (!imageAllowedExtensions.includes(fileType)) {
      return setError(true)
    }
    const reader = new FileReader()
    reader.onloadend = (e) => {
      if (e.target.readyState === FileReader.DONE) {
        const bytes = new Uint8Array(e.target.result)
        const isValid = validateFileHeader(bytes, fileType)
        if (isValid) {
          setError(false)
          handleFieldChange({ target: { name: 'image', value: file } })
        } else {
          setError(true)
        }
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateFile(e.dataTransfer.files[0])
    }
  }

  const openImagesDatabase = () => {
    handleFieldChange({ target: { name: 'imagesDatabaseOpen', value: true } })
  }

  const renderMessage = () => {
    if (error) {
      return {
        element: () => (
          <p className="error-message">{t('create-event:mediaUploadInput.errorUploadingPhoto')}</p>
        ),
        icon: 'cross',
        fill: '#CE2C21',
      }
    }
    if (!formState.image) {
      return {
        element: () => null,
        icon: 'dropzone',
        fill: 'none',
        svgProps: { stroke: 'black' },
      }
    }
    return {
      element: () => (
        <p className="success-message">
          {t('create-event:mediaUploadInput.fileUploadedSuccessfully', {
            fileName: formState.image.name ? formState.image.name : formState.image.id,
          })}
        </p>
      ),
      icon: 'checkmark',
      fill: '#548671',
      viewBox: '0 0 40 30',
    }
  }

  const { element, icon, fill, viewBox, svgProps } = renderMessage()

  return (
    <form id="file-upload" onDragEnter={handleDrag} onSubmit={(e) => e.preventDefault()}>
      <input hidden type="file" id="input-file-upload" />
      <div
        className="dropzone"
        id="drag-file-element"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Icon icon={icon} viewBox={viewBox || null} svgProps={svgProps || null} fill={fill} />
        {element()}
        {formState.image ? (
          <p className="header">{t('create-event:mediaUploadInput.dragToUpdateImage')}</p>
        ) : (
          <p>{t('create-event:mediaUploadInput.dropPhotosHere')}</p>
        )}
        <p className="info">
          {t('create-event:mediaUploadInput.orPrefix')}{' '}
          <span role="button" onClick={openImagesDatabase} className="link">
            {t('create-event:mediaUploadInput.browseLibraryLink')}
          </span>
        </p>
      </div>
    </form>
  )
}

MediaUploadInput.propTypes = {
  eventName: PropTypes.string,
  handleFieldChange: PropTypes.func,
  formState: PropTypes.object,
}

export default MediaUploadInput
